import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Constants } from '../../common/constants';
import { IFileSizeLimitMap, IPermission, IScreenshot, ISideMenu, ISubscription, ISubscriptionDialogConfig, ISubscriptionResponseAPI, IUser, IUserReponseAPI, IWebsocketMessage } from '../../models';
import { CommonFunctions } from '../../classes/common-functions';
import { Permissions } from '../../classes/permissions';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../../services/auth/auth.service';
import { CommonService } from '../../services/common.service';
import { ScreenService } from '../../services/screen/screen.service';
import { SettingsService } from '../../services/settings/settings.service';
import { ToasterService } from '../../services/toaster/toaster.service';
import { WebsocketService } from '../../services/websocket/websocket.service';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { AddUserComponent } from '../../shared/add-user/add-user.component';
import { AvatarComponent } from '../../shared/avatar/avatar.component';
import { EmailVerificationPopupComponent } from '../../shared/email-verification-popup/email-verification-popup.component';
import { PlanUpdateComponent } from '../../shared/plan-update/plan-update.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { ProfileDialogComponent } from './profile-dialog/profile-dialog.component';
import { Subscription } from 'rxjs';
import { ChangePasswordComponent } from '../password/change-password/change-password.component';
import { SubcriptionExpiredDialogComponent } from './subcription-expired-dialog';

@Component({
  selector: 'app-topbar',
  imports: [
    MatListModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatMenuModule,
    AvatarComponent,
    ChangePasswordComponent,
    AddUserComponent
  ],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit, OnDestroy {

  applicationVersion: string = Constants.applicationVersion;
  applicationName: string = Constants.applicationName;

  profileDetail: IUser = {} as IUser;
  currentUser: IUser = {} as IUser;
  currentSubscription: ISubscription = {} as ISubscription;

  snackBarRef: MatSnackBarRef<TextOnlySnackBar> | null = null;

  isEmailVerificationDayLimitExceeded = false;
  isSubscriptionLoading = false;
  isChangePasswordPanelVisible = false;
  isEditProfilePanelVisible = false;

  subscriptions: Subscription[] = [];

  trialMinutes = 0;

  // Track MatDialogRefs to avoid multiple dialogs and allow closing on destroy
  profileDialogRef: MatDialogRef<ProfileDialogComponent> | null = null;
  screenshotViewerDialogRef: MatDialogRef<any> | null = null;
  emailVerificationDialogRef: MatDialogRef<EmailVerificationPopupComponent> | null = null;
  planUpdateDialogRef: MatDialogRef<PlanUpdateComponent> | null = null;
  subscriptionExpiredDialogRef: MatDialogRef<SubcriptionExpiredDialogComponent> | null = null;

  list: ISideMenu[] = [
    { name: Constants.screenNames.dashboard, path: Constants.routingPaths.dashboard, is_visible: true },
    { name: Constants.screenNames.screeens, path: Constants.routingPaths.screeens, is_visible: false },
    { name: Constants.screenNames.contents, path: Constants.routingPaths.contents, is_visible: false },
    { name: Constants.screenNames.compositions, path: Constants.routingPaths.compositions, is_visible: false },
    { name: Constants.screenNames.broadcasts, path: Constants.routingPaths.broadcasts, is_visible: false },
    { name: Constants.screenNames.apps, path: Constants.routingPaths.apps, is_visible: false },
    { name: Constants.screenNames.launcher, path: Constants.routingPaths.launcher, is_visible: false },
    { name: Constants.screenNames.templates, path: Constants.routingPaths.templates, is_visible: false },
    { name: Constants.screenNames.dzynflix, path: Constants.routingPaths.dzynflix, is_visible: false },
    { name: Constants.screenNames.reports, path: Constants.routingPaths.reports, is_visible: false },
    // { name: Constants.screenNames.users, path: Constants.routingPaths.users, is_visible: false },
    // { name: 'Settings', path: Constants.routingPaths.settings, is_visible:false },
  ];

  subsriptionDialogRef: MatDialogRef<SubcriptionExpiredDialogComponent> | null = null;

  @Output() toggleSidebar = new EventEmitter<void>();

  constructor(private authService: AuthService, private commonService: CommonService, private toaster: ToasterService, private dialog: MatDialog,
    private snackBar: MatSnackBar, private settingsService: SettingsService, private websocketService: WebsocketService, private screenService: ScreenService) {

    this.subscriptions.push(
      this.commonService.refreshSubscription.subscribe(() => this.loadSubscriptionDetails())
    );
    this.subscriptions.push(
      this.commonService.openChangePasswordSideNav.subscribe(() => this.isChangePasswordPanelVisible = true)
    );
    this.subscriptions.push(
      this.commonService.openUpgradePlanPopup.subscribe(() => this.openSubscriptionPopup())
    );
    this.subscriptions.push(
      this.commonService.openSubscriptionExpiredPopup.subscribe(() => this.openSubscriptionExpiredPopup())
    );
    this.subscriptions.push(
      this.commonService.openEditProfiledSideNav.subscribe(() => this.openEditProfile())
    );
    this.subscriptions.push(
      this.commonService.refreshProfileDetails.subscribe(() => this.refreshProfileDetail())
    );
  }

  ngOnInit(): void {
    this.loadProfileDetail();
    this.loadSubscriptionDetails();
    this.loadGeneralInformations();
  }

  ngOnDestroy(): void {
    this.websocketService.close();

    // Unsubscribe all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    // Close all open dialogs
    if (this.profileDialogRef) {
      this.profileDialogRef.close();
      this.profileDialogRef = null;
    }
    if (this.screenshotViewerDialogRef) {
      this.screenshotViewerDialogRef.close();
      this.screenshotViewerDialogRef = null;
    }
    if (this.emailVerificationDialogRef) {
      this.emailVerificationDialogRef.close();
      this.emailVerificationDialogRef = null;
    }
    if (this.planUpdateDialogRef) {
      this.planUpdateDialogRef.close();
      this.planUpdateDialogRef = null;
    }
    if (this.subscriptionExpiredDialogRef) {
      this.subscriptionExpiredDialogRef.close();
      this.subscriptionExpiredDialogRef = null;
    }

    // Dismiss snackbar if open
    this.dismissSnackbar();
  }

  get isSmallerScreen() {
    return window.innerWidth <= 900;
  }

  logout() {
    this.dismissSnackbar();
    this.authService.logout();
  }

  connectWebSocket() {
    this.websocketService.connect();
    const socket_message = {} as IWebsocketMessage;
    socket_message.client_type = "web";
    socket_message.user_id = this.commonService.profileDetail.id;
    this.websocketService.sendMessage(socket_message);
    this.subscribeToWebsocket();
  }

  subscribeToWebsocket() {
    this.subscriptions.push(
      this.websocketService.messagesSubject.subscribe({
        next: (message: IWebsocketMessage) => {
          // console.log(message);
          if (message && message.screenshot_id) {
            this.subscriptions.push(
              this.screenService.getScreenshotById(message.screenshot_id).subscribe({
                next: (response: IScreenshot) => {
                  // console.log(response);
                  this.toaster.successWithLink('Screenshot captured successfully', 'View Screenshot', () => {
                    if (response) {
                      this.viewScreenshot(response);
                    }
                  });
                },
                error: (error: HttpErrorResponse) => {
                  console.log(error);
                }
              })
            );
          }
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        }
      })
    );
  }

  viewScreenshot(element: IScreenshot) {
    CommonFunctions.openMediaPreviewPopup(element.file_upload, element.name || 'Screenshot Preview', '.png');
  }

  openProfileDialog(): void {
    if (this.profileDialogRef) {
      // Already open
      return;
    }
    this.profileDialogRef = this.dialog.open(ProfileDialogComponent, {
      width: '300px',
      position: { top: '60px', right: '20px' },
      panelClass: 'profile-dialog',
      data: {
        fullName: this.profileDetail.full_name,
        isSmallerScreen: this.isSmallerScreen
      }
    });

    this.profileDialogRef.afterClosed().subscribe(() => {
      this.profileDialogRef = null;
    });
  }

  refreshProfileDetail() {
    this.subscriptions.push(
      this.commonService.getProfileDetails().subscribe({
        next: (data: IUserReponseAPI) => {
          // console.log(data);
          this.profileDetail = data?.data ? data.data : {} as IUser;
          this.commonService.profileDetail = this.profileDetail;
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        }
      })
    );
  }

  loadProfileDetail() {
    this.commonService.businessConfig = { config_data: { launcher_menu_limit: 10 } };
    this.subscriptions.push(this.commonService.loadBusinessConfiguration().subscribe({
      error: () => { /* Use the default limit until the configuration API is available. */ }
    }));
    this.subscriptions.push(
      this.commonService.getProfileDetails().subscribe({
        next: (data: IUserReponseAPI) => {
          // console.log(data);
          this.profileDetail = data?.data ? data.data : {} as IUser;
          this.commonService.profileDetail = this.profileDetail;
          if (this.profileDetail.is_admin) {
            this.commonService.userPermission = [];
            this.commonService.isPermissionLoaded = true;
            this.setPermissions();
          }
          else {
            this.loadPermissionDetail();
          }
          this.connectWebSocket();
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        }
      })
    );
  }

  openEditProfile() {
    this.commonService.isLoading = true;
    this.subscriptions.push(
      this.commonService.getProfileDetails().subscribe({
        next: (data: IUserReponseAPI) => {
          // console.log(data);
          this.currentUser = data?.data ? data.data : {} as IUser;
          this.isEditProfilePanelVisible = true;
          this.commonService.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
          this.commonService.isLoading = false;
        }
      })
    );
  }

  loadSubscriptionDetails() {
    this.isSubscriptionLoading = true;
    this.subscriptions.push(
      this.commonService.getSubscriptionDetails().subscribe({
        next: (data: ISubscriptionResponseAPI) => {
          // console.log(data);
          const subscription = data.data;

          this.currentSubscription = subscription;
          this.commonService.subscriptionDetail = subscription;

          this.trialMinutes = CommonFunctions.getTrialsRemainingMinutes(subscription);
          this.isSubscriptionLoading = false;

          if (this.trialMinutes <= 0) {
            this.openSubscriptionExpiredPopup();
          }

        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
          this.isSubscriptionLoading = false;
        }
      })
    );
  }

  loadGeneralInformations() {
    this.subscriptions.push(
      this.commonService.getMaxFileSizes().subscribe({
        next: (response: IFileSizeLimitMap) => {
          // console.log(response);
          this.commonService.maxFileSizes = response;
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        }
      })
    )
  }

  loadPermissionDetail() {
    if (this.profileDetail?.role_id) {
      this.subscriptions.push(
        this.settingsService.getPermissionByRoleID(this.profileDetail.role_id).subscribe({
          next: (permissions: IPermission[]) => {
            this.commonService.userPermission = permissions?.length > 0 ? permissions : [];
            this.commonService.isPermissionLoaded = true;
            this.setPermissions();
          },
          error: (error: HttpErrorResponse) => {
            console.log(error);
          }
        })
      );
    }
  }

  setPermissions() {
    this.list = this.list.map(x => {
      if (x.name != Constants.screenNames.dashboard && x.name != Constants.screenNames.users) {
        x.is_visible = Permissions.canView(x.name, this.commonService);
        if (x.name === Constants.screenNames.launcher) {
          x.is_visible = x.is_visible && this.commonService.isLauncherAvailable();
        }
      }
      else if (x.name === Constants.screenNames.users) {
        x.is_visible = !(this.profileDetail?.created_by);
      }
      return x;
    });
  }

  checkEmailVerification() {
    const diffDays = CommonFunctions.getDaysSinceBusinessCreated(this.profileDetail?.business_created_at);
    if (diffDays > Constants.minimumDaysForEmailVerification) {
      this.isEmailVerificationDayLimitExceeded = true;
      this.openBlockerPopup();
    }
    else {
      this.isEmailVerificationDayLimitExceeded = false;
      this.showPersistentSnackbar();
    }
  }

  openBlockerPopup() {
    if (this.emailVerificationDialogRef) {
      return; // Already open
    }
    this.emailVerificationDialogRef = this.dialog.open(EmailVerificationPopupComponent, {
      disableClose: true
    });

    this.emailVerificationDialogRef.afterClosed().subscribe((message: string) => {
      this.emailVerificationDialogRef = null;
      if (message != 'logout') {
        this.loadProfileDetail();
      }
    });
  }

  showPersistentSnackbar() {
    if (this.snackBarRef) {
      return;
    }

    this.snackBarRef = this.snackBar.open('⚠️ Email verification pending. Please verify your email.', 'Verify Now', {
      duration: 0,
      panelClass: ['warning-snackbar']
    });

    this.snackBarRef.onAction().subscribe(() => {
      this.dismissSnackbar();
      this.openBlockerPopup();
    });
  }

  dismissSnackbar() {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
      this.snackBarRef = null;
    }
  }

  getRemainingTrialDays() {
    const trialDays = Math.floor(this.trialMinutes / 1440) + 1; // 1440 minutes in a day
    if (trialDays > 1) {
      return `Your trial ends in ${trialDays} days`;
    }
    else if (trialDays >= 0) {
      return `Your trial ends today`;
    }
    else {
      return 'Your trial has been expired';
    }
  }

  getRemainingSubscriptionDays() {
    const today = new Date();
    const expiry = new Date(this.currentSubscription.expiry_date);

    // Calculate difference in milliseconds and convert to days
    const diffInMs = expiry.getTime() - today.getTime();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

    // Ensure non-negative days
    return Math.max(diffInDays, 0);
  }

  getRemainingSubscriptionMinutes() {
    const today = new Date();
    const expiry = new Date(this.currentSubscription.expiry_date);

    // Calculate difference in milliseconds and convert to minutes
    const diffInMs = expiry.getTime() - today.getTime();
    const diffInMinutes = Math.ceil(diffInMs / (1000 * 60));

    // Ensure non-negative minutes
    return Math.max(diffInMinutes, 0);
  }

  getRemainingSubscriptionDisplay() {
    const remainingSubscriptionMinutes = this.getRemainingSubscriptionMinutes();
    const remainingDays = Math.floor(remainingSubscriptionMinutes / 1440);

    if (remainingDays > 1) {
      return `Your subscription ends in ${remainingDays} days`;
    }
    else if (remainingDays === 1) {
      return `Your subscription ends in ${remainingDays} day`;
    }
    else if (remainingDays === 0) {
      return `Your subscription ends today`;
    }
    else {
      return 'Your subscription has been expired';
    }
  }

  openSubscriptionPopup() {
    if (this.planUpdateDialogRef) {
      // Already open
      return;
    }

    const screenCount = this.currentSubscription.screen_limit > Constants.subscriptionConstants.defaultNumberOfScreens
      ? this.currentSubscription.screen_limit
      : Constants.subscriptionConstants.defaultNumberOfScreens;

    const dialogConfig: ISubscriptionDialogConfig = {
      maxWidth: '800px',
      panelClass: 'glass-dialog-container',
      disableClose: true,
      data: {
        screenCount: screenCount
      }
    };

    this.planUpdateDialogRef = this.dialog.open(PlanUpdateComponent, dialogConfig);

    this.planUpdateDialogRef.afterClosed().subscribe(() => {
      this.planUpdateDialogRef = null;
    });
  }

  handleOutsideClick(event: Event): void {
    event.stopPropagation();
  }

  handleCloseEvent(event: string) {
    // console.log(event);
    if (event === 'close' || event === 'success') {
      this.isChangePasswordPanelVisible = false;
      this.isEditProfilePanelVisible = false;
    }
  }

  openSubscriptionExpiredPopup() {
    if (this.subscriptionExpiredDialogRef) {
      return;
    }

    this.subscriptionExpiredDialogRef = this.dialog.open(SubcriptionExpiredDialogComponent, {
      width: 'min(88vw, 560px)',
      maxWidth: 'calc(100vw - 24px)',
      minWidth: '0',
      panelClass: 'subscription-expired-dialog-panel',
      backdropClass: 'subscription-expired-dialog-backdrop',
      disableClose: true
    });

    this.subscriptionExpiredDialogRef.afterClosed().subscribe(() => {
      this.subscriptionExpiredDialogRef = null;
    });
  }
}
